import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@store/store";

import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import { productionCheck } from "@components/buttonGroupView/utils";

import { postSeekClarificationResponseThunk } from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";

import dayjs from "dayjs";

interface SenderDetails {
  sender_id: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  sender_role: string;
}

interface ClarificationRecord {
  id: number;
  application_id: string;
  message: string;
  date_time: string;
  sender_role: string;
  recipient_role: string;
  sender_details: SenderDetails;
}

interface Props {
  open: boolean;
  onClose: () => void;
  clarification: ClarificationRecord;
  onSuccess?: () => void;
}

const MAX_REMARKS = 2000;

const ProvideClarificationDrawer: React.FC<Props> = ({
  open,
  onClose,
  clarification,
  onSuccess,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [remarks, setRemarks] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const isRow = window.location.pathname.includes("row");

  const getFullName = (sender?: SenderDetails) =>
    [sender?.first_name, sender?.middle_name, sender?.last_name]
      .filter(Boolean)
      .join(" ");

  const getInitials = (sender?: SenderDetails) =>
    `${sender?.first_name?.[0] ?? ""}${sender?.last_name?.[0] ?? ""}`;

  const handleFileUpload = async (file: File) => {
    try {
      const uploadRes: any = await dispatch(
        genericUploadThunk({
          file,
          moduleType: isRow ? "row" : "access",
        }),
      );

      const payload = uploadRes?.payload;

      if (!payload?.success) return;

      if (!productionCheck && payload?.data?.signedUrl) {
        await dispatch(
          uploadToS3Thunk({
            signedUrl: payload.data.signedUrl,
            file,
            contentType: file.type,
          }),
        );
      }

      setUploadedFiles((prev) => [
        ...prev,
        {
          fileName: payload.data.fileName,
          originalFileName: file.name,
          objectKey: payload.data.objectKey,
          mimeType: "pdf",
        },
      ]);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      const payload = {
        application_id: clarification.application_id,

        recipient_id: clarification.sender_details.sender_id,

        recipient_role: clarification.sender_role,

        message: remarks,

        clk_documents: uploadedFiles.map((file) => ({
          originalFileName: file?.originalFileName,
          fileName: file?.fileName,
          objectKey: file?.objectKey,
          mimeType: "pdf",
        })),
      };

      const response = await dispatch(
        postSeekClarificationResponseThunk(payload),
      );

      if (postSeekClarificationResponseThunk.fulfilled.match(response)) {
        setRemarks("");
        setUploadedFiles([]);

        onSuccess?.();

        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: {
            xs: "100%",
            sm: 500,
          },
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            Provide clarification to {clarification.sender_role}
          </Typography>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 3,
          }}
        >
          {/* Authority Message */}
          <Box
            sx={{
              p: 2,
              borderRadius: "8px",
              backgroundColor: "#F5F5F5",
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: "#CC8801",
                    fontSize: 12,
                  }}
                >
                  {getInitials(clarification.sender_details)}
                </Avatar>

                <Box>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                    }}
                  >
                    {getFullName(clarification.sender_details)}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#757575",
                    }}
                  >
                    {clarification.sender_role}
                  </Typography>
                </Box>
              </Box>

              <Typography
                sx={{
                  fontSize: 11,
                  color: "#757575",
                }}
              >
                {dayjs(clarification.date_time).format("DD MMM YY, h:mm A")}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: 14,
                color: "#212121",
              }}
            >
              {clarification.message}
            </Typography>
          </Box>

          {/* Remarks */}
          <Typography
            sx={{
              fontWeight: 700,
              mb: 1,
            }}
          >
            Remarks *
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={8}
            placeholder="Add remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value.slice(0, MAX_REMARKS))}
          />

          <Typography
            sx={{
              textAlign: "right",
              mt: 0.5,
              color: "#757575",
              fontSize: 12,
            }}
          >
            {remarks.length}/{MAX_REMARKS}
          </Typography>

          {/* Uploaded Files */}
          {uploadedFiles.map((file, index) => (
            <Box
              key={index}
              sx={{
                mt: 2,
                p: 1.5,
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                }}
              >
                {file.originalFileName}
              </Typography>

              <IconButton
                size="small"
                onClick={() =>
                  setUploadedFiles(uploadedFiles.filter((_, i) => i !== index))
                }
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Box>
          ))}

          {/* Upload */}
          <Box
            component="label"
            sx={{
              mt: 3,
              border: "1px dashed #D8D5E7",
              borderRadius: "8px",
              py: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
            }}
          >
            <input
              hidden
              type="file"
              accept=".pdf"
              onChange={(e) => {
                const file = e.target.files?.[0];

                if (file) {
                  handleFileUpload(file);
                }
              }}
            />

            <UploadFileIcon />

            <Typography
              sx={{
                fontSize: 12,
                color: "#3A1C73",
              }}
            >
              Upload Document - optional (.pdf)
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            borderTop: "1px solid #E5E7EB",
          }}
        >
          <Button
            fullWidth
            variant="contained"
            disabled={!remarks.trim() || submitting}
            onClick={handleSubmit}
            sx={{
              borderRadius: "999px",
              textTransform: "none",
              backgroundColor: "#3A1C73",
              py: 1,
              fontSize: 14,
            }}
          >
            Provide Clarification
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ProvideClarificationDrawer;
